/**
 * Asset Preloader
 * Preloads images and assets so they don't need to load during visualization
 */

import fluffleProjects from '@/data/fluffleProjects.json';

// Static assets to preload
const STATIC_ASSETS = [
  '/rabbit.webp',
  '/pixel.svg',
];

// Extract all project images from fluffleProjects
function getProjectImages(): string[] {
  return fluffleProjects
    .map((p: { img?: string }) => p.img)
    .filter((img): img is string => !!img && img.length > 0);
}

// Cache to keep preloaded images in memory (prevents garbage collection)
const imageCache: HTMLImageElement[] = [];

/**
 * Preload a single image and keep it in memory
 */
function preloadImage(src: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Keep reference to prevent garbage collection
      imageCache.push(img);
      resolve();
    };
    img.onerror = () => {
      // Don't fail on error, just continue
      resolve();
    };
    img.src = src;
  });
}

/**
 * Preload all assets
 * Returns a promise that resolves when all critical assets are loaded
 */
export async function preloadAssets(
  onProgress?: (loaded: number, total: number) => void
): Promise<void> {
  const projectImages = getProjectImages();
  const allAssets = [...STATIC_ASSETS, ...projectImages];
  const total = allAssets.length;
  let loaded = 0;

  // console.log(`🖼️ Preloading ${total} assets...`);

  // Preload static assets first (critical)
  const staticPromises = STATIC_ASSETS.map(async (src) => {
    await preloadImage(src);
    loaded++;
    onProgress?.(loaded, total);
  });

  await Promise.all(staticPromises);
  // console.log(`✅ Static assets loaded (${STATIC_ASSETS.length})`);

  // Preload project images in batches to avoid overwhelming the browser
  const BATCH_SIZE = 10;
  for (let i = 0; i < projectImages.length; i += BATCH_SIZE) {
    const batch = projectImages.slice(i, i + BATCH_SIZE);
    const batchPromises = batch.map(async (src) => {
      await preloadImage(src);
      loaded++;
      onProgress?.(loaded, total);
    });
    await Promise.all(batchPromises);
  }

  // console.log(`✅ All ${total} assets preloaded`);
}

/**
 * Preload only critical assets (fast, blocks rendering)
 */
export async function preloadCriticalAssets(): Promise<void> {
  // console.log('🖼️ Preloading critical assets...');
  await Promise.all(STATIC_ASSETS.map(preloadImage));
  // console.log('✅ Critical assets loaded');
}

/**
 * Preload project images in background (non-blocking)
 */
export function preloadProjectImagesInBackground(): void {
  const projectImages = getProjectImages();
  // console.log(`🖼️ Preloading ${projectImages.length} project images in background...`);

  // Load in small batches with delays to not impact performance
  const BATCH_SIZE = 5;
  const BATCH_DELAY = 100; // ms between batches

  let index = 0;

  function loadNextBatch() {
    if (index >= projectImages.length) {
      // console.log('✅ Background image preload complete');
      return;
    }

    const batch = projectImages.slice(index, index + BATCH_SIZE);
    batch.forEach(preloadImage);
    index += BATCH_SIZE;

    setTimeout(loadNextBatch, BATCH_DELAY);
  }

  loadNextBatch();
}
